"use client";

import React from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { ProductVariant } from "@/hooks/useFunnelState";
import { OfferResponse } from "@/types/simpego-api";
import { BaseCoverageId } from "@/config/coverages";
import { isPetYoungerThanAgeBand } from "@/utils/coveragePayload";
import {
  Card,
  CardContent,
  CardHeader,
} from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import PetPeek from "./PetPeek";
import { buildRows, TableRow } from "@/utils/coverageRows";
import { RowValue, RowLabel, SectionInfo } from "./CoverageRowParts";

type BundleKey = "S" | "M" | "L";

/** Size shown on the mobile tabs; the backend bundle keys (S/M/L) stay unchanged. */
const TAB_LABELS: Record<ProductVariant["id"], string> = { s: "M", m: "L", l: "XL" };

interface ProductVariantTableProps {
  productVariants: ProductVariant[];
  selectedProduct: ProductVariant | null;
  bundleRecommendations: { S: OfferResponse; M: OfferResponse; L: OfferResponse } | null;
  coverageSelections: Partial<Record<BaseCoverageId, boolean>>;
  /** Decides which Kostenübernahme (up to 9 years / from 9 years) is the headline figure. */
  petBirthDate?: Date | null;
  /** Cat or dog peeking over the table above the favourite package. */
  petType?: "cat" | "dog" | null;
  recommendedId?: "s" | "m" | "l";
  onSelectOnly: (product: ProductVariant) => void;
  onSelectAndSkipToContact: (product: ProductVariant) => void;
  onSelectAndCustomize: (product: ProductVariant) => void;
  isLoadingPrice: boolean;
  paymentInterval: 1 | 2 | 4 | 12;
  isFetchingRecommendations: boolean;
}

const ProductVariantTable: React.FC<ProductVariantTableProps> = ({
  productVariants,
  selectedProduct,
  bundleRecommendations,
  coverageSelections,
  petBirthDate,
  petType,
  recommendedId,
  onSelectOnly,
  onSelectAndSkipToContact,
  onSelectAndCustomize,
  isLoadingPrice,
  isFetchingRecommendations,
}) => {
  const { t } = useTranslation();

  // A cat or dog peeks over the table's top edge above the favourite once the prices are there, and only once.
  const [catPeeking, setCatPeeking] = React.useState(true);
  const pricesReady = !isFetchingRecommendations && !!bundleRecommendations;
  // Table columns are `2.2fr` for the labels plus one `1fr` per package, so the favourite's centre is:
  const favouriteIndex = productVariants.findIndex((v) => v.id === recommendedId);
  const favouriteCentre = favouriteIndex < 0 ? null : ((2.2 + favouriteIndex + 0.5) / (2.2 + productVariants.length)) * 100;

  const isYoung = isPetYoungerThanAgeBand(petBirthDate ?? null);
  const rows = React.useMemo(() => buildRows(coverageSelections, isYoung), [coverageSelections, isYoung]);

  const offerFor = (variant: ProductVariant): OfferResponse | null =>
    bundleRecommendations?.[variant.id.toUpperCase() as BundleKey] ?? null;

  const priceFor = (variant: ProductVariant): number | null => offerFor(variant)?.grossPremiumProRata ?? null;

  const Actions = ({ variant, compact }: { variant: ProductVariant; compact?: boolean }) => {
    const isSelected = selectedProduct?.id === variant.id;
    return (
      <div className="flex flex-col gap-2 w-full">
        {/* "Auswählen" accepts this tier as-is and skips straight to contact details,
            bypassing the coverage customization page — distinct from onSelectOnly, which just
            marks a tier active when switching mobile tabs, without navigating anywhere. */}
        <Button
          onClick={() => onSelectAndSkipToContact(variant)}
          className={cn(
            "w-full",
            isSelected
              ? "bg-continue-button text-continue-button-foreground hover:bg-continue-button/90"
              : "bg-primary text-primary-foreground hover:bg-primary/90",
            compact && "text-base py-2"
          )}
          disabled={isLoadingPrice || isFetchingRecommendations}
        >
          {isSelected ? t("priceCustomization.table.selectedPlan") : t("priceCustomization.table.selectPlan")}
        </Button>
        <button
          onClick={() => onSelectAndCustomize(variant)}
          disabled={isLoadingPrice || isFetchingRecommendations}
          className="text-sm font-semibold text-primary underline underline-offset-2 disabled:opacity-50"
        >
          {t("priceCustomization.table.customize")}
        </button>
      </div>
    );
  };

  // `price` (grossPremiumProRata) always comes from a recommendation fetched at a fixed
  // monthly frequency (see fetchBundleRecommendations, which hardcodes frequency: 12) — it's
  // already the monthly amount regardless of the customer's currently selected paymentInterval,
  // so it must be shown as-is here, not rescaled by paymentInterval like the live step-3 price.
  const PriceDisplay = ({ variant }: { variant: ProductVariant }) => {
    const price = priceFor(variant);
    if (isFetchingRecommendations || price === null) {
      return (
        <span className="inline-flex items-center gap-2 text-primary">
          <Loader2 className="h-5 w-5 animate-spin" />
          {t("common.loading")}
        </span>
      );
    }
    return (
      <span className="flex flex-wrap items-baseline justify-center gap-x-1.5 text-destructive md:justify-start">
        <span className="whitespace-nowrap">{`CHF ${price.toFixed(2)}`}</span>
        <span className="whitespace-nowrap text-sm font-medium text-muted-foreground">/ {t("common.monthly")}</span>
      </span>
    );
  };

  const RecommendedBadge = () => (
    <div className="absolute top-0 left-0 right-0 z-10 py-1.5 text-xs font-bold bg-primary text-primary-foreground text-center rounded-t-[inherit]">
      {t("priceCustomization.table.ourRecommendation")}
    </div>
  );

  const renderValue = (row: Exclude<TableRow, { kind: "section" }>, variant: ProductVariant, alignLeft = false) => (
    <RowValue row={row} offer={offerFor(variant)} t={t} loading={isFetchingRecommendations} alignLeft={alignLeft} />
  );

  /** Mobile: label and value on one line, like the desktop rows. */
  const renderRow = (row: TableRow, rowIndex: number, variant: ProductVariant, isSelected: boolean) => {
    const border = cn("border-b", isSelected ? "border-primary/20" : "border-border");

    if (row.kind === "section") {
      return (
        <div
          key={`section-${rowIndex}`}
          className={cn("px-3 py-2 flex items-center gap-2 text-foreground", row.sub ? "text-lg font-medium" : "bg-muted/30 text-xl font-semibold", border)}
        >
          <span>{t(row.labelKey)}</span>
          <SectionInfo row={row} t={t} />
        </div>
      );
    }

    return (
      <div key={`${variant.id}-row-${rowIndex}`} className={cn("flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-3 py-2", row.nested && "pl-8", border)}>
        <span className="min-w-min flex-1 text-lg">
          <RowLabel row={row} t={t} />
        </span>
        <span className="ml-auto min-w-0 max-w-full text-right">{renderValue(row, variant)}</span>
      </div>
    );
  };

  return (
    <>
      {/* Mobile: tabs layout */}
      <div className="md:hidden">
        <Tabs
          defaultValue={selectedProduct?.id ?? productVariants[0]?.id}
          onValueChange={(val) => {
            const variant = productVariants.find((p) => p.id === val);
            if (variant) onSelectOnly(variant);
          }}
          className="w-full"
        >
          <TabsList className="variant-tabs grid grid-cols-3 gap-2 bg-transparent p-0 relative after:content-[''] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-primary">
            {productVariants.map((variant) => (
              <TabsTrigger
                key={variant.id}
                value={variant.id}
                className={cn(
                  "variant-tab",
                  "relative text-xl font-bold py-4 rounded-lg border-2 transition-colors",
                  "border-border bg-muted/20",
                  "data-[state=active]:bg-background",
                  "data-[state=active]:border-primary",
                  "data-[state=active]:border-b-background",
                  "data-[state=active]:rounded-b-none",
                  "data-[state=active]:mb-[-2px]"
                )}
              >
                {TAB_LABELS[variant.id]}
              </TabsTrigger>
            ))}
          </TabsList>

          {productVariants.map((variant) => {
            const isSelected = selectedProduct?.id === variant.id;
            const isRecommended = recommendedId === variant.id;
            return (
              <TabsContent key={variant.id} value={variant.id} className="relative mt-[-2px]">
                <Card className={cn("relative overflow-hidden border-2 rounded-t-none", isSelected ? "border-primary shadow-lg" : "border-border", "border-t-0")}>
                  {isRecommended && <RecommendedBadge />}
                  <CardHeader className={cn("relative px-6 pb-3 text-center bg-background", isRecommended ? "pt-10" : "pt-6")}>
                    <h4 className="text-2xl font-bold mb-1">{variant.name}</h4>
                    <p className="text-sm text-muted-foreground mb-2">{variant.description}</p>
                    <p className="text-xl font-bold mb-3 whitespace-nowrap">
                      <PriceDisplay variant={variant} />
                    </p>
                    <Actions variant={variant} />
                  </CardHeader>

                  <CardContent className="p-0">
                    <div className="w-full bg-background">
                      {rows.map((row, rowIndex) => renderRow(row, rowIndex, variant, isSelected))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            );
          })}
        </Tabs>
      </div>

      {/* Desktop: one table, four columns — labels on the left, one column per package. Rows are thin
          (one line each), the header carries name and price, the buttons sit at the bottom. Only the selected
          package is marked by its "Ausgewählt" button; the recommended one by the corner ribbon. */}
      <div className="relative mt-6 hidden md:block">
       {catPeeking && pricesReady && favouriteCentre !== null && (
        <PetPeek species={petType ?? null} leftPercent={favouriteCentre} onDone={() => setCatPeeking(false)} />
       )}
       <div className="relative z-10 overflow-clip rounded-lg border border-border bg-background">
        <div className="grid" style={{ gridTemplateColumns: "minmax(200px, 1.6fr) repeat(3, minmax(0, 1fr))" }}>
          {(() => {
            // Row hover is pure CSS: the `contents` wrapper of a row is the hover group, so hovering never re-renders
            // the table (a re-render would remount the inline-defined buttons between mousedown and mouseup).
            const cell = "border-b border-border px-3 py-2 transition-colors duration-150 group-hover/row:bg-muted/50";
            return (
              <>
                <div className="sticky top-(--sticky-top) z-20 border-b border-border bg-background shadow-[0_4px_6px_rgb(0_0_0/0.10)] [clip-path:inset(0_0_-12px_0)]" />
                {productVariants.map((variant) => (
                  <div key={`head-${variant.id}`} className="sticky top-(--sticky-top) z-20 shadow-[0_4px_6px_rgb(0_0_0/0.10)] [clip-path:inset(0_0_-12px_0)] border-b border-border bg-background px-3 pl-4 pb-2 pt-10 text-left">
                    {recommendedId === variant.id && (
                      <span className="pointer-events-none absolute inset-x-0 top-0 bg-continue-button py-1.5 text-center text-sm font-semibold text-continue-button-foreground">
                        {t("priceCustomization.table.ourRecommendation")}
                      </span>
                    )}
                    <h4 className="text-base font-bold leading-tight">{variant.name}</h4>
                    <p className="text-2xl font-bold leading-tight whitespace-nowrap"><PriceDisplay variant={variant} /></p>
                  </div>
                ))}

                {rows.map((row, rowIndex) =>
                  row.kind === "section" ? (
                    <div key={`s-${rowIndex}`} className={cn("col-span-4 flex items-center gap-2 border-b border-border px-3 py-2", row.sub ? "text-lg font-medium" : "bg-muted/30 text-xl font-semibold")}>
                      <span>{t(row.labelKey)}</span>
                      <SectionInfo row={row} t={t} />
                    </div>
                  ) : (
                    <div key={`r-${rowIndex}`} className="group/row contents">
                      <div className={cn(cell, "flex items-center text-lg leading-snug", row.nested && "pl-8")}>
                        <span>
                          <RowLabel row={row} t={t} />
                        </span>
                      </div>
                      {productVariants.map((variant) => (
                        <div key={`${variant.id}-${rowIndex}`} className={cn(cell, "flex items-center justify-start pl-4 text-left")}>
                          {renderValue(row, variant, true)}
                        </div>
                      ))}
                    </div>
                  )
                )}

                <div />
                {productVariants.map((variant) => (
                  <div key={`foot-${variant.id}`} className="px-3 py-3">
                    <Actions variant={variant} compact />
                  </div>
                ))}
              </>
            );
          })()}
        </div>
       </div>
      </div>
    </>
  );
};

export default ProductVariantTable;
